import React from 'react'
import { IVolym3Brukare, IVolym3Manad } from '../../gen/api'
import { bestalltIdagPerKategori, bestalltPerKategori, datumtext, ejFardigregistrerad, enDecimal, harBestallning, manadsnamn, procent, tillText, utfortTotalt } from './format'

// Tooltips i Volymuppföljning (pejlen-produkt#107): Beställd tid i dag (A2, A9) och månadscellen (A22).
// En överblick, inga detaljer (Anton 2026-10-08): tiden per insatskategori med summan på rubrikraden.
// Beställd tid i dag visar också Från, Till och Ändrad. Detaljerna hör hemma i en flik på brukarens sida.

const tabell: React.CSSProperties = { borderCollapse: 'collapse', width: '100%' }
const rubrik: React.CSSProperties = { fontWeight: 600, paddingTop: 6 }
const indrag: React.CSSProperties = { paddingLeft: 12, paddingRight: 10, whiteSpace: 'nowrap' }
const tal: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: 10 }
const dampad: React.CSSProperties = { opacity: 0.75 }
const kolumnnamn: React.CSSProperties = { fontWeight: 400, opacity: 0.75, paddingBottom: 2 }
const namnkolumn: React.CSSProperties = { paddingRight: 16, whiteSpace: 'nowrap' }
const varde: React.CSSProperties = { whiteSpace: 'nowrap' }

// ---------- Beställd tid i dag (A2, A9) ----------

export const IdagTooltip = ({ brukare, idag }: { brukare: IVolym3Brukare; idag: string }) => {
  if (brukare.bestalldIdag === null) {
    if (brukare.sistaBestalldaDag && brukare.sistaBestalldaDag < idag) return <>Avslutad {datumtext(brukare.sistaBestalldaDag)}</>
    return <>Ingen beställning gäller i dag</>
  }
  // Datumen gäller beställningar och slås ihop per kategori: tidigaste Från, första Till och senaste Ändrad.
  // Ändrad är den senaste ändringsbeställningen, oavsett om tiden ändrades. Kolumnen visas bara när minst en
  // kategori har en ändringsbeställning (Anton 2026-10-08).
  const kategorier = bestalltIdagPerKategori(brukare.idagDelar)
  const visaAndrad = kategorier.some((k) => k.andrad !== null)
  // Beställd tid står på en egen rad (Anton 2026-10-08). Med flera kategorier följer en tabell med ett
  // tabellhuvud på alla kolumner. Med en kategori följer en kort lista, så att timmarna inte upprepas.
  return (
    <div>
      <div style={{ fontWeight: 600 }}>Aktuella beställningar idag, {datumtext(idag)}</div>
      <div style={{ fontWeight: 600, paddingTop: 6 }}>Beställd tid: {enDecimal(brukare.bestalldIdag)} h/mån</div>
      {kategorier.length > 1 && (
        <table style={{ ...tabell, marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ ...kolumnnamn, ...namnkolumn, textAlign: 'left' }}>Insatskategori</th>
              <th style={{ ...kolumnnamn, ...tal }}>Omfattning</th>
              <th style={{ ...kolumnnamn, ...tal }}>Från</th>
              <th style={{ ...kolumnnamn, ...tal }}>Till</th>
              {visaAndrad && <th style={{ ...kolumnnamn, ...tal }}>Ändrad</th>}
            </tr>
          </thead>
          <tbody>
            {kategorier.map((k) => (
              <tr key={k.kategori}>
                <td style={namnkolumn}>{k.kategori}</td>
                <td style={tal}>{enDecimal(k.taktManad)} h/mån</td>
                <td style={tal}>{datumtext(k.fran)}</td>
                <td style={tal}>{tillText(k.till, idag)}</td>
                {visaAndrad && <td style={tal}>{k.andrad ? datumtext(k.andrad) : '–'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {kategorier.length === 1 && (
        <table style={{ ...tabell, marginTop: 8 }}>
          <tbody>
            <tr>
              <td style={{ ...namnkolumn, ...dampad }}>Insatskategori</td>
              <td style={varde}>{kategorier[0].kategori}</td>
            </tr>
            <tr>
              <td style={{ ...namnkolumn, ...dampad }}>Från</td>
              <td style={varde}>{datumtext(kategorier[0].fran)}</td>
            </tr>
            <tr>
              <td style={{ ...namnkolumn, ...dampad }}>Till</td>
              <td style={varde}>{tillText(kategorier[0].till, idag)}</td>
            </tr>
            {kategorier[0].andrad && (
              <tr>
                <td style={{ ...namnkolumn, ...dampad }}>Ändrad</td>
                <td style={varde}>{datumtext(kategorier[0].andrad)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------- Månadscellen (A22) ----------
// Utförd tid delas i planerade och oplanerade besök, och utförandegraden visas med uträkningen.

export const ManadTooltip = ({ manad, senasteBesok }: { manad: IVolym3Manad; senasteBesok: string | null }) => {
  const kategorier = bestalltPerKategori(manad)
  const utfort = utfortTotalt(manad)
  const p = procent(manad)

  return (
    <div>
      <div style={{ fontWeight: 600 }}>{manadsnamn(manad.ym)}</div>
      <table style={tabell}>
        <tbody>
          <tr>
            <td style={rubrik}>Beställd tid</td>
            <td style={{ ...tal, ...rubrik }}>{enDecimal(manad.bestalld)} h</td>
          </tr>
          {kategorier.length === 0 && (
            <tr>
              <td colSpan={2} style={{ ...indrag, ...dampad }}>
                Ingen beställning den här månaden
              </td>
            </tr>
          )}
          {kategorier.map((k) => (
            <tr key={k.kategori}>
              <td style={indrag}>{k.kategori}</td>
              <td style={tal}>{enDecimal(k.timmar)} h</td>
            </tr>
          ))}

          <tr>
            <td style={rubrik}>Utförd tid</td>
            <td style={{ ...tal, ...rubrik }}>{enDecimal(utfort)} h</td>
          </tr>
          <tr>
            <td style={indrag}>Planerade besök</td>
            <td style={tal}>{enDecimal(manad.utfordPlanerad)} h</td>
          </tr>
          <tr>
            <td style={indrag}>Oplanerade besök</td>
            <td style={tal}>{enDecimal(manad.utfordOplanerad)} h</td>
          </tr>
          {ejFardigregistrerad(manad.ym, senasteBesok) && (
            <tr>
              <td colSpan={2} style={{ ...indrag, ...dampad }}>
                Utfört hittills, t.o.m. {datumtext(senasteBesok as string)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div style={{ fontWeight: 600, paddingTop: 8 }}>
        {harBestallning(manad) ? `Utförandegrad: ${enDecimal(utfort)} / ${enDecimal(manad.bestalld)} = ${p} %` : 'Ingen beställning, ingen utförandegrad'}
      </div>
    </div>
  )
}
